import React, { useEffect } from "react";
import { createPortal } from "react-dom";
import styles from "./SophiaReadyDialog.module.css";
import HangingStars from "./HangingStars";
import AnimatedButton from "../AnimatedButton/AnimatedButton";



export default function SophiaReadyDialog({
  open,
  onClose,
  businessName = "your business",
  agentName = "Sophia",
  /*
   * Agent ka apna avatar. Pehle dono render branches me
   * "/svg/receptionist_profile.png" hardcoded tha, isliye har agent — chahe
   * uska avatar Female-01 ho ya Female-05 — yahan ek hi generic tasveer
   * dikhata tha.
   *
   * Default wahi purani image hai, to jo callers ye prop nahi bhejte unke
   * liye kuch nahi badla.
   */
  agentAvatar = "/svg/receptionist_profile.png",
  mode = "ready",

  // ── ready mode props ────────────────────────────────────────────
  /*
   * "What she just did" checklist. `[{ key, label, done }]`.
   * Each row renders a green check when `done`, an amber cross when not,
   * so a step the user SKIPPED is not reported back as configured.
   * Omitted / empty → the legacy all-green three-row list, so any caller
   * that hasn't been updated keeps its previous rendering exactly.
   */
  readyItems = null,
  calendarConnected = false,
  onContinue,
  onGoDashboard,
  // "Improve {agent}"    add-on cards — optional click handlers. When
  // provided, the card becomes clickable and routes to the matching
  // setup surface (spam filtering → Contacts, calendar → Connect
  // Calendar). When omitted the card stays display-only (legacy).
  onSpamFilter,
  onConnectCalendar,
  // Free minutes granted this month (shown in the green banner).
  freeMinutes = 50,
  isTrial = false,
  trialMinutes = 150,
  trialDays = 21,
  isPayg = false,

  // ── setupSummary mode props ─────────────────────────────────────
  loading = false,
  setupItems = [],

  onOkay,
}) {

  useEffect(() => {
    if (!open) return;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e) => {
      if (mode === "setupSummary") return; // blocked in summary mode
      if (e.key === "Escape" && typeof onClose === "function") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prevOverflow;
      window.removeEventListener("keydown", onKey);
    };
  }, [open, onClose, mode]);

  if (!open) return null;

  // No fallback list here on purpose. This used to default to three green
  // ticks, which claimed call forwarding and triage were configured even when
  // the user had skipped both. The caller owns the real state; if it passes
  // nothing we show nothing rather than inventing a result.
  const resolvedReadyItems = Array.isArray(readyItems) ? readyItems : [];

  // Turns an "Improve {agent}" add-on card into a button when a handler
  // is supplied (keyboard-activatable + pointer cursor). No handler =>
  // card stays a plain, non-interactive div.
  const cardClickProps = (handler) =>
    typeof handler === "function"
      ? {
          onClick: handler,
          role: "button",
          tabIndex: 0,
          onKeyDown: (e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              handler();
            }
          },
          style: { cursor: "pointer" },
        }
      : {};

  // Body par portal — wizard ke scroll container (.body) ke andar iOS Safari
  // fixed backdrop ko usi tak seemit rakhta tha, TopStepper upar dikhta tha.
  return createPortal(
    <div
      className={styles.backdrop}
      role="dialog"
      aria-modal="true"
      aria-labelledby="sophia-ready-title"
    >
      <div className={styles.sheet}>
        {mode === "setupSummary" ? (

      
          <>
            <div className={styles.summaryHeaderRow}>
              <div className={styles.avatarWrap} aria-hidden="true">
                <img
                  src={agentAvatar}
                  alt=""
                  className={styles.avatarImg}
                  onError={(e) => {
                    // Avatar 404 kare (file hat gayi / path galat) to generic
                    // tasveer pe gir jao — dialog toota hua na dikhe.
                    e.currentTarget.src = "/svg/receptionist_profile.png";
                  }}
                />
                <span className={styles.avatarBadge} aria-hidden="true" />
              </div>
              <div className={styles.summaryHeaderText}>
                <h3 id="sophia-ready-title" className={styles.summaryTitle}>
                  <span className={styles.summaryTitleAccent}>{agentName}</span>{" "}
                  is Live!
                </h3>
                <p className={styles.summarySubtitle}>
                  <span className={styles.summarySubtitleAccent}>
                    {businessName}
                  </span>{" "}
                  is now open for calls{" "}
                  <span className={styles.summarySubtitleAccent}>24/7.</span>
                </p>
              </div>
            </div>

            <div className={styles.summaryCard}>
              <h4 className={styles.summaryCardTitle}>Your Setup Summary</h4>

              {loading || !setupItems || setupItems.length === 0 ? (
                <ul className={styles.summaryList}>
                  {[1, 2, 3, 4, 5, 6].map((n) => (
                    <li key={`skeleton-${n}`} className={styles.summaryItem}>
                      <span className={`${styles.summarySkeletonIcon} ${styles.shimmer}`} aria-hidden="true" />
                      <div className={styles.summaryItemText}>
                        <div
                          className={styles.shimmer}
                          style={{
                            width: n % 2 === 0 ? "40%" : "52%",
                            height: 14,
                            borderRadius: 4,
                          }}
                        />
                        <div
                          className={styles.shimmer}
                          style={{
                            width: n % 2 === 0 ? "65%" : "45%",
                            height: 12,
                            borderRadius: 4,
                            marginTop: 6,
                          }}
                        />
                      </div>
                      {(n === 3 || n === 5) && (
                        <div
                          className={`${styles.summarySkeletonBtn} ${styles.shimmer}`}
                          style={{
                            width: 86,
                            height: 32,
                            borderRadius: 999,
                            flexShrink: 0,
                          }}
                        />
                      )}
                    </li>
                  ))}
                </ul>
              ) : (
                <ul className={styles.summaryList}>
                  {setupItems.map((item) => (
                    <li key={item.key} className={styles.summaryItem}>
                      <span className={styles.summaryIconWrap} aria-hidden="true">
                        {item.icon}
                        {item.status === "done" ? (
                          <span
                            className={styles.summaryStatusCheck}
                            aria-hidden="true"
                          >
                            <svg width="9" height="9" viewBox="0 0 16 16" fill="none">
                              <path
                                d="M3.5 8L6.5 11L12.5 5"
                                stroke="#fff"
                                strokeWidth="2.6"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                            </svg>
                          </span>
                        ) : (
                          <span
                            className={styles.summaryStatusInfo}
                            aria-hidden="true"
                          >
                            <span className={styles.summaryStatusInfoGlyph}>i</span>
                          </span>
                        )}
                      </span>

                      <div className={styles.summaryItemText}>
                        <div className={styles.summaryItemLabel}>{item.label}</div>
                        <div
                          className={
                            item.status === "done"
                              ? styles.summaryItemValueDone
                              : styles.summaryItemValuePending
                          }
                        >
                          {item.value}
                        </div>
                      </div>

                      {item.status === "pending" &&
                        typeof item.onAction === "function" && (
                          <button
                            type="button"
                            className={styles.summarySetupNowBtn}
                            onClick={item.onAction}
                          >
                            Setup now
                          </button>
                        )}
                    </li>
                  ))}
                </ul>
              )}
            </div>

            <AnimatedButton
              label="Okay"
              onClick={onOkay}
              positionType="relative"
              width="100%"
              bottom="0px"
            />
          </>
        ) : (
          // ────────────────────────────────────────────────────────
          // ready mode (legacy GoLive / PostPurchase Triage exit)
          // ────────────────────────────────────────────────────────
          <>
            <div className={styles.headerRow}>
              <div className={styles.avatarWrap} aria-hidden="true">
                <img
                  src={agentAvatar}
                  alt=""
                  className={styles.avatarImg}
                  onError={(e) => {
                    e.currentTarget.src = "/svg/receptionist_profile.png";
                  }}
                />
                <span className={styles.avatarBadge} aria-hidden="true" />
              </div>
              <div className={styles.headerText}>
                <h3 id="sophia-ready-title" className={styles.title}>
                  {agentName} is live
                </h3>
                <p className={styles.titleSub}>and answering your calls</p>
                <p className={styles.subtitle}>
                  Your AI receptionist is now catching every call for{" "}
                  <b className={styles.bizName}>{businessName}</b> 24/7.
                </p>
              </div>
            </div>

            <div className={styles.liveStatusRow}>
              <ul className={styles.checkList}>
                {resolvedReadyItems.map((item) => (
                  <li key={item.key} className={styles.checkRow}>
                    <span
                      className={
                        item.done ? styles.checkBadge : styles.checkBadgePending
                      }
                      aria-hidden="true"
                    >
                      {item.done ? (
                        <svg width="11" height="11" viewBox="0 0 16 16" fill="none">
                          <path
                            d="M3.5 8L6.5 11L12.5 5"
                            stroke="#fff"
                            strokeWidth="2.4"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      ) : (
                        <svg width="11" height="11" viewBox="0 0 16 16" fill="none">
                          <path
                            d="M5 5l6 6M11 5l-6 6"
                            stroke="#fff"
                            strokeWidth="2.4"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      )}
                    </span>
                    <span
                      className={
                        item.done ? styles.checkLabel : styles.checkLabelPending
                      }
                    >
                      {item.label}
                    </span>
                  </li>
                ))}
              </ul>

              {/* Latakte taare — asli vector SVG (saaf), animation ke saath.
                  Pehle yahan /svg/stars-new.svg ki tasveer thi. */}
              <span className={styles.starsDecor} aria-hidden="true">
                <HangingStars />
              </span>
            </div>

            {/* Banner: Trial (150 minutes for 21 days) vs $5/PAYG (50 free minutes) */}
            <div className={styles.freeMinutesBanner}>
              <span className={styles.freeMinutesIcon} aria-hidden="true">
                {isTrial ? (
                  
                    <img src="/svg/trial-strip.svg" alt="trial-strip-icon" />
                ) : (
                  <img src="/svg/money_tickect_icon.svg" alt="money_tickect_icon" />
                )}
              </span>
              <div className={styles.freeMinutesText}>
                {isTrial ? (
                  <p className={styles.freeMinutesTitle}>
                    You've got {trialMinutes} minutes for Next {trialDays} Days
                  </p>
                ) : (
                  <p className={styles.freeMinutesTitle}>
                    You've got <b>{freeMinutes} free minutes</b> this month
                  </p>
                )}
                <p className={styles.freeMinutesSub}>
                  Every call {agentName} answers is business you won't miss.
                </p>
              </div>
            </div>

            <h4 className={styles.evenBetterHeading}>
              Improve {agentName} with<span><svg width="136" height="22" viewBox="0 0 136 22" fill="none" xmlns="http://www.w3.org/2000/svg">
                <rect y="1" width="136" height="20" rx="6" fill="#009A1C" />
                <path d="M24.6098 16V5.81818H30.9237V7.14062H26.146V10.2429H30.4713V11.5604H26.146V16H24.6098ZM32.8129 16V5.81818H36.4422C37.231 5.81818 37.8856 5.95407 38.406 6.22585C38.9297 6.49763 39.3208 6.87382 39.5793 7.3544C39.8378 7.83168 39.9671 8.38352 39.9671 9.00994C39.9671 9.63305 39.8361 10.1816 39.5743 10.6555C39.3158 11.1262 38.9247 11.4924 38.401 11.7543C37.8807 12.0161 37.2261 12.147 36.4372 12.147H33.6879V10.8246H36.298C36.7952 10.8246 37.1995 10.7533 37.5111 10.6108C37.826 10.4683 38.0563 10.2611 38.2021 9.98935C38.348 9.71757 38.4209 9.3911 38.4209 9.00994C38.4209 8.62547 38.3463 8.29238 38.1972 8.01065C38.0513 7.72893 37.821 7.51349 37.5061 7.36435C37.1946 7.21188 36.7852 7.13565 36.2781 7.13565H34.3492V16H32.8129ZM37.8392 11.4062L40.3548 16H38.6048L36.1389 11.4062H37.8392ZM41.8364 16V5.81818H48.2199V7.14062H43.3726V10.2429H47.8868V11.5604H43.3726V14.6776H48.2796V16H41.8364ZM50.2719 16V5.81818H56.6555V7.14062H51.8081V10.2429H56.3224V11.5604H51.8081V14.6776H56.7151V16H50.2719ZM63.2726 16H61.642L65.306 5.81818H67.0809L70.7449 16H69.1143L66.2357 7.66761H66.1562L63.2726 16ZM63.5461 12.0128H68.8358V13.3054H63.5461V12.0128ZM75.5115 16H72.2153V5.81818H75.6159C76.6135 5.81818 77.4703 6.02202 78.1862 6.42969C78.9021 6.83404 79.4506 7.41572 79.8318 8.17472C80.2162 8.9304 80.4085 9.83688 80.4085 10.8942C80.4085 11.9548 80.2146 12.8662 79.8268 13.6286C79.4423 14.3909 78.8855 14.9775 78.1563 15.3885C77.4272 15.7962 76.5455 16 75.5115 16ZM73.7515 14.6577H75.4269C76.2025 14.6577 76.8472 14.5118 77.3609 14.2202C77.8746 13.9252 78.2591 13.4993 78.5143 12.9425C78.7695 12.3823 78.8971 11.6996 78.8971 10.8942C78.8971 10.0954 78.7695 9.41761 78.5143 8.8608C78.2624 8.30398 77.8862 7.88139 77.3857 7.59304C76.8853 7.30469 76.2638 7.16051 75.5214 7.16051H73.7515V14.6577ZM85.615 16H82.3188V5.81818H85.7194C86.717 5.81818 87.5738 6.02202 88.2897 6.42969C89.0056 6.83404 89.5541 7.41572 89.9353 8.17472C90.3198 8.9304 90.512 9.83688 90.512 10.8942C90.512 11.9548 90.3181 12.8662 89.9303 13.6286C89.5458 14.3909 88.989 14.9775 88.2599 15.3885C87.5307 15.7962 86.6491 16 85.615 16ZM83.855 14.6577H85.5305C86.306 14.6577 86.9507 14.5118 87.4644 14.2202C87.9781 13.9252 88.3626 13.4993 88.6178 12.9425C88.873 12.3823 89.0006 11.6996 89.0006 10.8942C89.0006 10.0954 88.873 9.41761 88.6178 8.8608C88.3659 8.30398 87.9897 7.88139 87.4893 7.59304C86.9888 7.30469 86.3673 7.16051 85.6249 7.16051H83.855V14.6577ZM96.8023 10.2628V11.5554H92.2881V10.2628H96.8023ZM107.747 10.9091C107.747 11.9962 107.548 12.9309 107.151 13.7131C106.753 14.492 106.208 15.0919 105.515 15.5128C104.826 15.9304 104.042 16.1392 103.163 16.1392C102.282 16.1392 101.495 15.9304 100.802 15.5128C100.113 15.0919 99.569 14.4903 99.1713 13.7081C98.7735 12.9259 98.5747 11.9929 98.5747 10.9091C98.5747 9.82197 98.7735 8.88897 99.1713 8.11008C99.569 7.32789 100.113 6.72798 100.802 6.31037C101.495 5.88944 102.282 5.67898 103.163 5.67898C104.042 5.67898 104.826 5.88944 105.515 6.31037C106.208 6.72798 106.753 7.32789 107.151 8.11008C107.548 8.88897 107.747 9.82197 107.747 10.9091ZM106.226 10.9091C106.226 10.0805 106.092 9.38281 105.823 8.81605C105.558 8.24598 105.194 7.8151 104.729 7.52344C104.269 7.22846 103.747 7.08097 103.163 7.08097C102.577 7.08097 102.053 7.22846 101.592 7.52344C101.132 7.8151 100.767 8.24598 100.499 8.81605C100.234 9.38281 100.101 10.0805 100.101 10.9091C100.101 11.7377 100.234 12.437 100.499 13.0071C100.767 13.5739 101.132 14.0047 101.592 14.2997C102.053 14.5914 102.577 14.7372 103.163 14.7372C103.747 14.7372 104.269 14.5914 104.729 14.2997C105.194 14.0047 105.558 13.5739 105.823 13.0071C106.092 12.437 106.226 11.7377 106.226 10.9091ZM117.881 5.81818V16H116.469L111.293 8.53267H111.199V16H109.663V5.81818H111.084L116.265 13.2955H116.359V5.81818H117.881ZM125.725 10.228L124.377 10.4666C124.321 10.2943 124.231 10.1302 124.109 9.97443C123.989 9.81865 123.827 9.69105 123.622 9.59162C123.416 9.49219 123.159 9.44247 122.851 9.44247C122.43 9.44247 122.079 9.53693 121.797 9.72585C121.515 9.91146 121.374 10.1518 121.374 10.4467C121.374 10.7019 121.469 10.9074 121.658 11.0632C121.847 11.219 122.152 11.3466 122.573 11.446L123.786 11.7244C124.488 11.8868 125.012 12.1371 125.357 12.4751C125.701 12.8132 125.874 13.2524 125.874 13.7926C125.874 14.25 125.741 14.6577 125.476 15.0156C125.214 15.3703 124.848 15.6487 124.377 15.8509C123.91 16.053 123.368 16.1541 122.752 16.1541C121.896 16.1541 121.199 15.9718 120.658 15.6072C120.118 15.2393 119.787 14.7173 119.664 14.0412L121.101 13.8224C121.19 14.197 121.374 14.4804 121.653 14.6726C121.931 14.8615 122.294 14.956 122.742 14.956C123.229 14.956 123.618 14.8549 123.91 14.6527C124.202 14.4472 124.347 14.197 124.347 13.902C124.347 13.6634 124.258 13.4628 124.079 13.3004C123.903 13.138 123.633 13.0154 123.269 12.9325L121.976 12.6491C121.263 12.4867 120.736 12.2282 120.395 11.8736C120.057 11.5189 119.888 11.0698 119.888 10.5263C119.888 10.0755 120.014 9.68111 120.266 9.34304C120.518 9.00497 120.866 8.74148 121.31 8.55256C121.754 8.36032 122.263 8.2642 122.836 8.2642C123.661 8.2642 124.311 8.44318 124.785 8.80114C125.259 9.15578 125.572 9.63139 125.725 10.228Z" fill="white" />
                <path d="M16.2363 16.2363C13.2473 19.2254 8.40113 19.2254 5.41211 16.2363C2.42308 13.2473 2.42308 8.40113 5.41211 5.41211C8.40113 2.42308 13.2473 2.42308 16.2363 5.41211C19.2254 8.40113 19.2254 13.2473 16.2363 16.2363ZM11.3026 6.76514C11.3026 6.50094 11.0884 6.28677 10.8242 6.28677C10.56 6.28677 10.3459 6.50094 10.3459 6.76514V10.3459H6.76514C6.50094 10.3459 6.28677 10.56 6.28677 10.8242C6.28677 11.0884 6.50094 11.3026 6.76514 11.3026L10.3459 11.3026L10.3459 14.8833C10.3459 15.1475 10.56 15.3617 10.8242 15.3617C11.0884 15.3617 11.3026 15.1475 11.3026 14.8833L11.3026 11.3026L14.8833 11.3026C15.1475 11.3026 15.3617 11.0884 15.3617 10.8242C15.3617 10.56 15.1475 10.3459 14.8833 10.3459H11.3026V6.76514Z" fill="#00C524" stroke="white" stroke-linejoin="round" />
              </svg></span>
            </h4>

            <div className={styles.featureRow}>
              <div className={styles.featureItem} {...cardClickProps(onSpamFilter)}>
                <div className={styles.featureHead}>
                  <span className={styles.featureIconWrap}>
                    <svg width="42" height="38" viewBox="0 0 42 38" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <circle cx="19" cy="19" r="19" fill="#F4F2FD" />
                      <path d="M26.5597 11.9022L19.226 9.14328C19.0793 9.08766 18.9198 9.08766 18.773 9.14328L11.4402 11.9022C11.1768 12.0021 11 12.2604 11 12.5508V16.1364C11 21.6324 13.9505 26.5708 18.7038 29.029C18.7968 29.077 18.897 29.1016 19 29.1016C19.103 29.1016 19.2033 29.077 19.2962 29.029C24.0495 26.5699 27 21.6322 27 16.1364V12.5508C27 12.2614 26.8231 12.003 26.5597 11.9022ZM23.3369 16.4505L18.817 21.8126C18.6903 21.9644 18.5071 22.0502 18.3139 22.0502H18.3102C18.1133 22.0464 17.9301 21.9606 17.8071 21.805L14.9933 18.3778C14.7536 18.0846 14.7864 17.65 15.0699 17.4058C15.3497 17.1578 15.7699 17.1955 16.0069 17.485L18.3202 20.3022L22.3307 15.5475C22.5741 15.258 22.9943 15.2307 23.2704 15.4786C23.5502 15.7304 23.5766 16.1649 23.3369 16.4505Z" fill="#6524EB" />
                      <path d="M37.555 29.5556C35.4277 31.6829 31.9786 31.6829 29.8513 29.5556C27.7239 27.4283 27.7239 23.9792 29.8513 21.8519C31.9786 19.7245 35.4277 19.7245 37.555 21.8519C39.6823 23.9792 39.6823 27.4283 37.555 29.5556ZM34.0436 22.8148C34.0436 22.6268 33.8912 22.4744 33.7031 22.4744C33.5151 22.4744 33.3627 22.6268 33.3627 22.8148V25.3633L30.8142 25.3633C30.6262 25.3633 30.4738 25.5157 30.4738 25.7037C30.4738 25.8918 30.6262 26.0442 30.8142 26.0442L33.3627 26.0442L33.3627 28.5926C33.3627 28.7807 33.5151 28.9331 33.7031 28.9331C33.8912 28.9331 34.0436 28.7807 34.0436 28.5926L34.0436 26.0442L36.592 26.0442C36.7801 26.0442 36.9325 25.8918 36.9325 25.7037C36.9325 25.5157 36.7801 25.3633 36.592 25.3633L34.0436 25.3633V22.8148Z" fill="#00C524" stroke="white" stroke-linejoin="round" />
                    </svg>

                  </span>
                  <p className={styles.featureTitle}>Turn on spam filtering</p>
                </div>
                <p className={styles.featureSub}>
                  block junk, send VIPs straight to you
                </p>
              </div>

              <div className={styles.featureItem} {...cardClickProps(onConnectCalendar)}>
                <div className={styles.featureHead}>
                  <span className={styles.featureIconWrap}>
                    <svg width="42" height="38" viewBox="0 0 42 38" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <circle cx="19" cy="19" r="19" fill="#F4F2FD" />
                      <path d="M11.0742 13.7782V25.2529C11.0742 26.0105 11.7606 26.6242 12.608 26.6242H25.4427C26.2901 26.6242 26.9765 26.0105 26.9765 25.2529V13.7782C26.9765 13.0207 26.2901 12.407 25.4427 12.407H23.7809V11.7083C23.7809 11.3737 23.4765 11.1016 23.1023 11.1016C22.728 11.1016 22.4236 11.3737 22.4236 11.7083V12.407H15.6272V11.7083C15.6272 11.3737 15.3227 11.1016 14.9485 11.1016C14.5742 11.1016 14.2698 11.3737 14.2698 11.7083V12.407H12.608C11.7606 12.407 11.0742 13.0207 11.0742 13.7782ZM22.4236 13.5615V14.4769C22.4236 14.8115 22.728 15.0836 23.1023 15.0836C23.4765 15.0836 23.7809 14.8115 23.7809 14.4769V13.5615C24.1125 13.7592 24.3316 14.0955 24.3316 14.4769C24.3316 15.0836 23.7809 15.576 23.1023 15.576C22.4236 15.576 21.8729 15.0836 21.8729 14.4769C21.8729 14.0938 22.092 13.7574 22.4236 13.5615ZM14.2698 13.5615V14.4769C14.2698 14.8115 14.5742 15.0836 14.9485 15.0836C15.3227 15.0836 15.6272 14.8115 15.6272 14.4769V13.5615C15.9587 13.7592 16.1778 14.0955 16.1778 14.4769C16.1778 15.0836 15.6272 15.576 14.9485 15.576C14.2698 15.576 13.7191 15.0836 13.7191 14.4769C13.7191 14.0938 13.9382 13.7574 14.2698 13.5615ZM13.215 16.6057H24.8377C25.2605 16.6057 25.6037 16.9126 25.6037 17.2905V24.7086C25.6037 25.0865 25.2605 25.3934 24.8377 25.3934H13.213C12.7903 25.3934 12.4471 25.0865 12.4471 24.7086V17.2922C12.4471 16.9143 12.7903 16.6075 13.213 16.6075L13.215 16.6057Z" fill="#6524EB" />
                      <path d="M18.0123 17.4258H17.006C16.8646 17.4258 16.75 17.5282 16.75 17.6546V18.5544C16.75 18.6807 16.8646 18.7832 17.006 18.7832H18.0123C18.1537 18.7832 18.2683 18.6807 18.2683 18.5544V17.6546C18.2683 17.5282 18.1537 17.4258 18.0123 17.4258Z" fill="#6524EB" />
                      <path d="M21.0436 17.4258H20.0372C19.8958 17.4258 19.7812 17.5282 19.7812 17.6546V18.5544C19.7812 18.6807 19.8958 18.7832 20.0372 18.7832H21.0436C21.1849 18.7832 21.2995 18.6807 21.2995 18.5544V17.6546C21.2995 17.5282 21.1849 17.4258 21.0436 17.4258Z" fill="#6524EB" />
                      <path d="M24.0826 17.4258H23.0763C22.9349 17.4258 22.8203 17.5282 22.8203 17.6546V18.5544C22.8203 18.6807 22.9349 18.7832 23.0763 18.7832H24.0826C24.224 18.7832 24.3386 18.6807 24.3386 18.5544V17.6546C24.3386 17.5282 24.224 17.4258 24.0826 17.4258Z" fill="#6524EB" />
                      <path d="M14.9733 20.1211H13.9669C13.8255 20.1211 13.7109 20.2235 13.7109 20.3499V21.2497C13.7109 21.376 13.8255 21.4785 13.9669 21.4785H14.9733C15.1146 21.4785 15.2292 21.376 15.2292 21.2497V20.3499C15.2292 20.2235 15.1146 20.1211 14.9733 20.1211Z" fill="#6524EB" />
                      <path d="M18.0123 20.1211H17.006C16.8646 20.1211 16.75 20.2235 16.75 20.3499V21.2497C16.75 21.376 16.8646 21.4785 17.006 21.4785H18.0123C18.1537 21.4785 18.2683 21.376 18.2683 21.2497V20.3499C18.2683 20.2235 18.1537 20.1211 18.0123 20.1211Z" fill="#6524EB" />
                      <path d="M21.0436 20.1211H20.0372C19.8958 20.1211 19.7812 20.2235 19.7812 20.3499V21.2497C19.7812 21.376 19.8958 21.4785 20.0372 21.4785H21.0436C21.1849 21.4785 21.2995 21.376 21.2995 21.2497V20.3499C21.2995 20.2235 21.1849 20.1211 21.0436 20.1211Z" fill="#6524EB" />
                      <path d="M24.0826 20.1211H23.0763C22.9349 20.1211 22.8203 20.2235 22.8203 20.3499V21.2497C22.8203 21.376 22.9349 21.4785 23.0763 21.4785H24.0826C24.224 21.4785 24.3386 21.376 24.3386 21.2497V20.3499C24.3386 20.2235 24.224 20.1211 24.0826 20.1211Z" fill="#6524EB" />
                      <path d="M14.9733 22.8125H13.9669C13.8255 22.8125 13.7109 22.915 13.7109 23.0413V23.9411C13.7109 24.0675 13.8255 24.1699 13.9669 24.1699H14.9733C15.1146 24.1699 15.2292 24.0675 15.2292 23.9411V23.0413C15.2292 22.915 15.1146 22.8125 14.9733 22.8125Z" fill="#6524EB" />
                      <path d="M18.0084 22.8125H17.0021C16.8607 22.8125 16.7461 22.915 16.7461 23.0413V23.9411C16.7461 24.0675 16.8607 24.1699 17.0021 24.1699H18.0084C18.1498 24.1699 18.2644 24.0675 18.2644 23.9411V23.0413C18.2644 22.915 18.1498 22.8125 18.0084 22.8125Z" fill="#6524EB" />
                      <path d="M21.0436 22.8125H20.0372C19.8958 22.8125 19.7812 22.915 19.7812 23.0413V23.9411C19.7812 24.0675 19.8958 24.1699 20.0372 24.1699H21.0436C21.1849 24.1699 21.2995 24.0675 21.2995 23.9411V23.0413C21.2995 22.915 21.1849 22.8125 21.0436 22.8125Z" fill="#6524EB" />
                      <path d="M37.3011 31.5556C35.1738 33.6829 31.7247 33.6829 29.5973 31.5556C27.47 29.4283 27.47 25.9792 29.5973 23.8519C31.7247 21.7245 35.1738 21.7245 37.3011 23.8519C39.4284 25.9792 39.4284 29.4283 37.3011 31.5556ZM33.7897 24.8148C33.7897 24.6268 33.6372 24.4744 33.4492 24.4744C33.2612 24.4744 33.1088 24.6268 33.1088 24.8148V27.3633L30.5603 27.3633C30.3723 27.3633 30.2199 27.5157 30.2199 27.7037C30.2199 27.8918 30.3723 28.0442 30.5603 28.0442L33.1088 28.0442L33.1088 30.5926C33.1088 30.7807 33.2612 30.9331 33.4492 30.9331C33.6372 30.9331 33.7897 30.7807 33.7897 30.5926L33.7897 28.0442L36.3381 28.0442C36.5262 28.0442 36.6786 27.8918 36.6786 27.7037C36.6786 27.5157 36.5262 27.3633 36.3381 27.3633L33.7897 27.3633V24.8148Z" fill="#00C524" stroke="white" stroke-linejoin="round" />
                    </svg>

                  </span>
                  <p className={styles.featureTitle}>Connect your calendar</p>
                </div>
                <p className={styles.featureSub}>
                  let <strong>{agentName}</strong> book appointments while you work
                </p>
              </div>
            </div>

            <div className={styles.dashedDivider} aria-hidden="true" />

            <div className={styles.actionRow}>
              <button type="button" className={styles.dashboardOutlineBtn} onClick={onGoDashboard}>
              Do it Later
              <p className={styles.btnSubtext}>Go to Dashboard</p>
              </button>
              <button type="button" className={styles.completeFilledBtn} onClick={onContinue}>
                Get Add-Ons
                 <p className={styles.btnSubtext2}>make '{agentName}' better</p>
              </button>
            </div>
          </>
        )}
      </div>
    </div>,
    document.body
  );
}
